<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';
import SettingsSection from '../../../../../components/SettingsSection.vue';

export default {
  components: {
    SettingsSection,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isEnterprise } = useConfig();
    const { uiSettings, updateUISettings } = useUISettings();

    return { v$: useVuelidate(), isEnterprise, uiSettings, updateUISettings };
  },
  data() {
    return {
      selectedAgents: [],
      selectedTeamIds: [],
      teamMemberAgentIds: [],
      isAgentListUpdating: false,
      enableAutoAssignment: false,
      maxAssignmentLimit: null,
      isHydratingTeamFilter: false,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      teamList: 'teams/getTeams',
    }),
    selectedTeams: {
      get() {
        return this.teamList.filter(team =>
          this.selectedTeamIds.includes(team.id)
        );
      },
      set(teams) {
        this.selectedTeamIds = teams.map(team => team.id);
      },
    },
    filteredAgentList() {
      if (!this.selectedTeamIds.length) {
        return this.agentList;
      }

      const filteredAgents = this.agentList.filter(agent =>
        this.teamMemberAgentIds.includes(agent.id)
      );

      const selectedAgentsOutsideFilter = this.selectedAgents.filter(
        selectedAgent =>
          !filteredAgents.some(
            filteredAgent => filteredAgent.id === selectedAgent.id
          )
      );

      return [...selectedAgentsOutsideFilter, ...filteredAgents];
    },
    maxAssignmentLimitErrors() {
      if (this.v$.maxAssignmentLimit.$error) {
        return this.$t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
        );
      }
      return '';
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    selectedTeamIds: {
      async handler() {
        if (this.isHydratingTeamFilter) {
          return;
        }
        this.saveTeamFilterSettings();
        await this.fetchTeamMembersForSelectedTeams();
      },
      deep: true,
    },
  },
  async mounted() {
    this.$store.dispatch('agents/get');
    await this.$store.dispatch('teams/get');
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.enableAutoAssignment = this.inbox.enable_auto_assignment;
      this.maxAssignmentLimit =
        this.inbox?.auto_assignment_config?.max_assignment_limit || null;
      this.initializeTeamFilterFromSettings();
      this.fetchAttachedAgents();
    },
    initializeTeamFilterFromSettings() {
      const storedTeamFilter =
        this.uiSettings?.inbox_collaborators_team_filter?.[this.inbox.id] || [];
      this.isHydratingTeamFilter = true;
      this.selectedTeamIds = storedTeamFilter;
      this.fetchTeamMembersForSelectedTeams().finally(() => {
        this.isHydratingTeamFilter = false;
      });
    },
    saveTeamFilterSettings() {
      const currentTeamFilters =
        this.uiSettings?.inbox_collaborators_team_filter || {};
      this.updateUISettings({
        inbox_collaborators_team_filter: {
          ...currentTeamFilters,
          [this.inbox.id]: this.selectedTeamIds,
        },
      });
    },
    async fetchTeamMembersForSelectedTeams() {
      if (!this.selectedTeamIds.length) {
        this.teamMemberAgentIds = [];
        return;
      }

      await Promise.allSettled(
        this.selectedTeamIds.map(teamId =>
          this.$store.dispatch('teamMembers/get', {
            teamId,
          })
        )
      );

      const uniqueAgentIds = new Set();
      this.selectedTeamIds.forEach(teamId => {
        const teamMembers =
          this.$store.getters['teamMembers/getTeamMembers'](teamId);
        teamMembers.forEach(member => uniqueAgentIds.add(member.id));
      });
      this.teamMemberAgentIds = [...uniqueAgentIds];
    },
    async fetchAttachedAgents() {
      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inbox.id,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        this.selectedAgents = inboxMembers;
      } catch (error) {
        //  Handle error
      }
    },
    handleEnableAutoAssignment() {
      this.updateInbox();
    },
    async updateAgents() {
      const agentList = this.selectedAgents.map(el => el.id);
      this.isAgentListUpdating = true;
      try {
        await this.$store.dispatch('inboxMembers/create', {
          inboxId: this.inbox.id,
          agentList,
        });
        useAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
      this.isAgentListUpdating = false;
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          enable_auto_assignment: this.enableAutoAssignment,
          auto_assignment_config: {
            max_assignment_limit: this.maxAssignmentLimit,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      }
    },
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
    maxAssignmentLimit: {
      minValue: minValue(1),
    },
  },
};
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT')"
    >
      <multiselect
        v-model="selectedTeams"
        :options="teamList"
        track-by="id"
        label="name"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        :placeholder="$t('FORMS.MULTISELECT.SELECT')"
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
      />

      <multiselect
        v-model="selectedAgents"
        :options="filteredAgentList"
        track-by="id"
        label="name"
        multiple
        :close-on-select="false"
        :clear-on-select="false"
        hide-selected
        placeholder="Pick some"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        @select="v$.selectedAgents.$touch"
      />

      <woot-submit-button
        :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        :loading="isAgentListUpdating"
        @click="updateAgents"
      />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT_SUB_TEXT')"
    >
      <label class="w-3/4 settings-item">
        <div class="flex items-center gap-2">
          <input
            id="enableAutoAssignment"
            v-model="enableAutoAssignment"
            type="checkbox"
            @change="handleEnableAutoAssignment"
          />
          <label for="enableAutoAssignment">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT') }}
          </label>
        </div>

        <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT') }}
        </p>
      </label>

      <div
        v-if="enableAutoAssignment && isEnterprise"
        class="max-assignment-container"
      >
        <woot-input
          v-model="maxAssignmentLimit"
          type="number"
          :class="{ error: v$.maxAssignmentLimit.$error }"
          :error="maxAssignmentLimitErrors"
          :label="$t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT')"
          @blur="v$.maxAssignmentLimit.$touch"
        />

        <p class="pb-1 text-sm not-italic text-slate-600 dark:text-slate-400">
          {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT') }}
        </p>

        <woot-submit-button
          :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :disabled="v$.maxAssignmentLimit.$invalid"
          @click="updateInbox"
        />
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';
@import 'dashboard/assets/scss/mixins';

.max-assignment-container {
  padding-top: var(--space-slab);
  padding-bottom: var(--space-slab);
}
</style>
